<script lang="ts">
import type { InputTagsProps } from '@nuxt/ui';
import type { InputBlockProps } from '#layers/blocks/types/input-block';

interface InputTagsBlockProps extends
	InputBlockProps,
	Pick<InputTagsProps, 'size' | 'variant' | 'placeholder'> {
}

const __DsBlock = {
	type: 'input',
	name: '$t:blocks.input_tags.name',
	description: '$t:blocks.input_tags.description',
	icon: 'lucide:tags',
	category: 'input',
	tags: ['form-control'],
	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'placeholder',
			label: '$t:blocks.common.props.placeholder',
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
	],
}</script>

<script setup lang="ts">
import { computed } from 'vue';

import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice } from '#layers/ui/public';

const {
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
	size,
	variant,
	placeholder,
	readonly,
} = defineProps<InputTagsBlockProps>();
const model = defineModel<string[]>();

;

const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp }), { model, defaultValue: () => [] });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const forwarded = computed(() => ({ size, variant, placeholder, readonly }));
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DInputTags
			v-bind="forwarded"
			v-model="modelValue"
			:disabled
			:readonly
			:aria-readonly="readonly"
			:class="{ 'ring-default!': readonly }"
			:ui="{
				root: 'data-disabled:opacity-50 data-disabled:ring-default!',
				item: 'data-disabled:opacity-100',
				input: 'disabled:opacity-100',
			}"
		>
			<template #item-delete>
				<DIcon v-if="!readonly" name="lucide:x" />
				<!-- The empty <div> is necessary because NuxtUI renders a close icon if this slot renders empty -->
				<div v-else />
			</template>
		</DInputTags>
	</DTooltip>
</template>
